// Enhanced ShiftTaskModal.jsx with Holiday and Shift Timing Validation
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import AlertBox from '../Components/AlertBox';
import Snackbar from '../Components/Snackbar';
import ConfirmDialog from '../Components/ConfirmDialog';
import { AUTH_API_BASE_URL as API_BASE_URL } from '../LeadMng/config/api';

const ShiftTaskModal = ({ tasks = [], onClose, onShiftSuccess }) => {
  // Support both single task and multiple tasks
  const taskArray = useMemo(() => {
    return Array.isArray(tasks) ? tasks : [tasks].filter(Boolean);
  }, [tasks]);

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [shiftReason, setShiftReason] = useState('');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');
  const [isNextFollowUpManual, setIsNextFollowUpManual] = useState(false);

  // Date modification states
  const [updateDates, setUpdateDates] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newDueTime, setNewDueTime] = useState('');
  const [dateErrors, setDateErrors] = useState({});

  // NEW: Holiday and shift validation states (from task form)
  const [holidays, setHolidays] = useState([]);
  const [holidayError, setHolidayError] = useState('');
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [employeeShifts, setEmployeeShifts] = useState({});
  const [employeeHolidays, setEmployeeHolidays] = useState({});
  const [loadingShiftData, setLoadingShiftData] = useState(false);
  const [shiftValidationErrors, setShiftValidationErrors] = useState({});

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedFilterDepartment, setSelectedFilterDepartment] = useState('all');
  const [departmentMap, setDepartmentMap] = useState({});
  const [showAlert, setShowAlert] = useState(null);

  // Snackbar and ConfirmDialog states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info'
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: () => { }
  });
  const [dialogLoading, setDialogLoading] = useState(false);
  const authToken = useMemo(() => {
    return localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
  }, []);



  // Helper to get current user ID from token
  const getUserId = () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.userId || decoded.id || decoded._id;
      }
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
    return null;
  };


  // // Initialize dates from first task
  // useEffect(() => {
  //   if (hasInitializedDates.current || taskArray.length === 0) return;

  //   const firstTask = taskArray[0];

  //   console.log('🔄 Initializing dates from task:', firstTask._id);

  //   // Extract start date and time from the first task
  //   if (firstTask.startDateTime) {
  //     const startDate = new Date(firstTask.startDateTime);
  //     if (!isNaN(startDate.getTime())) {
  //       setNewStartDate(startDate.toISOString().split('T')[0]);
  //       setNewStartTime(startDate.toTimeString().slice(0, 5));
  //     }
  //   } else if (firstTask.startDate) {
  //     setNewStartDate(firstTask.startDate);
  //     setNewStartTime(firstTask.startTime || '09:00');
  //   }

  //   // Extract due date and time from the first task
  //   if (firstTask.endDateTime) {
  //     const endDate = new Date(firstTask.endDateTime);
  //     if (!isNaN(endDate.getTime())) {
  //       setNewDueDate(endDate.toISOString().split('T')[0]);
  //       setNewDueTime(endDate.toTimeString().slice(0, 5));
  //     }
  //   } else if (firstTask.deadline) {
  //     setNewDueDate(firstTask.deadline);
  //     setNewDueTime(firstTask.endTime || '18:00');
  //   }

  //   hasInitializedDates.current = true;
  //   console.log('✅ Dates initialized');
  // }, []);


  // Initialize dates from first task - FIXED timezone issue
  const hasInitializedDates = useRef(false);

  useEffect(() => {
    // Only initialize once
    if (hasInitializedDates.current) return;

    if (taskArray.length > 0) {
      const firstTask = taskArray[0];

      console.log('🔄 Initializing dates from task:', firstTask._id);

      // Extract start date and time from the first task
      if (firstTask.startDateTime) {
        const startDate = new Date(firstTask.startDateTime);
        if (!isNaN(startDate.getTime())) {
          // FIXED: Use local date methods to avoid timezone shift
          const year = startDate.getFullYear();
          const month = String(startDate.getMonth() + 1).padStart(2, '0');
          const day = String(startDate.getDate()).padStart(2, '0');
          const hours = String(startDate.getHours()).padStart(2, '0');
          const minutes = String(startDate.getMinutes()).padStart(2, '0');

          setNewStartDate(`${year}-${month}-${day}`);
          setNewStartTime(`${hours}:${minutes}`);

          console.log('✅ Start date initialized:', `${year}-${month}-${day}`, `${hours}:${minutes}`);
        }
      } else if (firstTask.startDate) {
        // Handle separate date field
        const dateStr = firstTask.startDate;
        if (dateStr) {
          // If it's already in YYYY-MM-DD format, use directly
          if (dateStr.includes('-')) {
            setNewStartDate(dateStr.split('T')[0]); // Remove time if present
          } else {
            // Parse and format if in different format
            const date = new Date(dateStr);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            setNewStartDate(`${year}-${month}-${day}`);
          }
        }
        setNewStartTime(firstTask.startTime || '09:00');
        console.log('✅ Start date initialized from separate fields');
      }

      // Extract due date and time from the first task
      if (firstTask.endDateTime) {
        const endDate = new Date(firstTask.endDateTime);
        if (!isNaN(endDate.getTime())) {
          // FIXED: Use local date methods to avoid timezone shift
          const year = endDate.getFullYear();
          const month = String(endDate.getMonth() + 1).padStart(2, '0');
          const day = String(endDate.getDate()).padStart(2, '0');
          const hours = String(endDate.getHours()).padStart(2, '0');
          const minutes = String(endDate.getMinutes()).padStart(2, '0');

          setNewDueDate(`${year}-${month}-${day}`);
          setNewDueTime(`${hours}:${minutes}`);

          console.log('✅ Due date initialized:', `${year}-${month}-${day}`, `${hours}:${minutes}`);
        }
      } else if (firstTask.deadline) {
        // Handle separate date field
        const dateStr = firstTask.deadline;
        if (dateStr) {
          // If it's already in YYYY-MM-DD format, use directly
          if (dateStr.includes('-')) {
            setNewDueDate(dateStr.split('T')[0]); // Remove time if present
          } else {
            // Parse and format if in different format
            const date = new Date(dateStr);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            setNewDueDate(`${year}-${month}-${day}`);
          }
        }
        setNewDueTime(firstTask.endTime || '18:00');
        console.log('✅ Due date initialized from separate fields');
      }

      // Mark as initialized
      hasInitializedDates.current = true;
      console.log('✅ Dates initialized successfully');
    }
  }, []); // Empty dependency - only run once


  // NEW: Fetch holidays from API (from task form)
  const fetchHolidays = async () => {
    try {
      setLoadingHolidays(true);
      const companyId = await getCompanyId();
      const token = authToken;

      if (!companyId) throw new Error('No company ID found for fetching holidays');
      if (!token) throw new Error('No authentication token found for fetching holidays');

      const url = `${API_BASE_URL}/holidays/company/${companyId}`;
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token,
        'x-access-token': token
      };

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (response.status === 401) {
        throw new Error('Authentication failed while fetching holidays');
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch holidays: ${response.status}`);
      }

      const data = await response.json();
      let holidayList = [];

      if (Array.isArray(data)) {
        holidayList = data;
      } else if (data && Array.isArray(data.holidays)) {
        holidayList = data.holidays;
      } else if (data && Array.isArray(data.data)) {
        holidayList = data.data;
      }

      // Extract holiday dates in YYYY-MM-DD format for easy comparison
      const holidayDates = holidayList.map(holiday => {
        const date = new Date(holiday.date || holiday.holidayDate);
        return date.toISOString().split('T')[0];
      });

      setHolidays(holidayDates);
      console.log('Holidays fetched successfully:', holidayDates);
      return holidayDates;
    } catch (error) {
      console.error('Holiday fetch failed:', error);
      setHolidayError(error.message);
      setHolidays([]);
      return [];
    } finally {
      setLoadingHolidays(false);
    }
  };

  // NEW: Fetch shift details by ID (from task form)
  const fetchShiftDetails = async (shiftId) => {
    try {
      const token = authToken;
      if (!token) throw new Error('No authentication token found');

      const url = `${API_BASE_URL}/shifts/${shiftId}`;
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token,
        'x-access-token': token
      };

      console.log('Fetching shift details for ID:', shiftId);

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch shift details: ${response.status}`);
      }

      const data = await response.json();
      console.log('Raw shift data:', data);

      // Handle different API response structures
      let shiftData = data;
      if (data.data) {
        shiftData = data.data;
      } else if (data.shift) {
        shiftData = data.shift;
      }

      console.log('Processed shift data:', shiftData);
      return shiftData;
    } catch (error) {
      console.error('Shift details fetch failed:', error);
      return null;
    }
  };

  // NEW: Fetch individual employee details including shift timing (from task form)
  const fetchEmployeeDetailsWithShift = async (employeeId) => {
    try {
      const token = authToken;
      if (!token) throw new Error('No authentication token found');

      const url = `${API_BASE_URL}/employees/${employeeId}`;
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token,
        'x-access-token': token
      };

      console.log('Fetching employee details for ID:', employeeId);

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch employee details: ${response.status}`);
      }

      const data = await response.json();
      console.log('Raw employee data:', data);

      // Handle different API response structures
      let employeeData = data;
      if (data.data) {
        employeeData = data.data;
      } else if (data.employee) {
        employeeData = data.employee;
      }

      // If employee has a shift ID, fetch the actual shift details
      if (employeeData.shift && typeof employeeData.shift === 'string') {
        console.log('Fetching shift timing for shift ID:', employeeData.shift);
        const shiftDetails = await fetchShiftDetails(employeeData.shift);

        if (shiftDetails) {
          // Merge shift timing into employee details
          employeeData.shiftDetails = {
            id: shiftDetails._id || shiftDetails.id,
            name: shiftDetails.name || shiftDetails.shiftName || 'Unknown Shift',
            startTime: shiftDetails.startTime,
            endTime: shiftDetails.endTime,
            duration: shiftDetails.duration,
            description: shiftDetails.description
          };
          console.log('Employee shift timing:', employeeData.shiftDetails);
        } else {
          console.warn('Could not fetch shift details for shift ID:', employeeData.shift);
        }
      }

      console.log('Final processed employee data:', employeeData);
      return employeeData;
    } catch (error) {
      console.error('Enhanced employee details fetch failed:', error);
      return null;
    }
  };

  // NEW: Helper function to convert time to 24-hour format for comparison (from task form)
  const convertTo24Hour = (timeStr) => {
    if (!timeStr) return 0;

    // Handle different time formats
    let time = timeStr.toString().trim();

    // Check if it already contains AM/PM
    const periodMatch = time.match(/(AM|PM|am|pm)/);
    let period = periodMatch ? periodMatch[1].toUpperCase() : null;

    // Remove period from time string
    if (period) {
      time = time.replace(/(AM|PM|am|pm)/, '');
    }

    // Split hours and minutes
    const [hoursStr, minutesStr] = time.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr || 0, 10);

    // Handle 12-hour format conversion
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    // If no period specified, assume 24-hour format

    // Convert to comparable number (e.g., 14:30 = 1430)
    return hours * 100 + minutes;
  };

  // NEW: Check if a date is a holiday (from task form)
  const isHoliday = (dateString) => {
    if (!dateString) return false;
    return holidays.includes(dateString);
  };

  // NEW: Check if a date is a holiday for an employee
  const isHolidayForEmployee = (date, employeeId) => {
    const holidayData = employeeHolidays[employeeId];
    if (!holidayData) return false;

    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Check weekly holidays
    if (holidayData.weeklyHoliday && holidayData.weeklyHoliday.includes(dayOfWeek)) {
      return true;
    }

    // Check specific holiday dates
    const dateString = dateObj.toISOString().split('T')[0];
    return holidayData.holidays.some(holiday => {
      const holidayDate = new Date(holiday.date || holiday).toISOString().split('T')[0];
      return holidayDate === dateString;
    });
  };

  // NEW: Check if time falls within shift hours
  const isWithinShiftHours = (time, employeeId) => {
    const shiftData = employeeShifts[employeeId];
    if (!shiftData) return true; // If no shift data, assume it's okay

    const timeMinutes = convertTo24Hour(time);
    const startMinutes = convertTo24Hour(shiftData.startTime);
    const endMinutes = convertTo24Hour(shiftData.endTime);

    // Handle overnight shifts
    if (endMinutes < startMinutes) {
      return timeMinutes >= startMinutes || timeMinutes <= endMinutes;
    }

    return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
  };

  // NEW: Validate task assignment against employee schedule with actual shift timing (from task form)
  const validateEmployeeSchedule = async (selectedEmployees, startDate, startTime, endTime) => {
    const violations = [];

    // Helper function to get day name safely
    const getDayName = (date) => {
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      return days[date.getDay()];
    };

    // Helper function to normalize day names for comparison
    const normalizeDayName = (dayName) => {
      const dayMap = {
        'sun': 'sunday', 'sunday': 'sunday',
        'mon': 'monday', 'monday': 'monday',
        'tue': 'tuesday', 'tuesday': 'tuesday',
        'wed': 'wednesday', 'wednesday': 'wednesday',
        'thu': 'thursday', 'thursday': 'thursday',
        'fri': 'friday', 'friday': 'friday',
        'sat': 'saturday', 'saturday': 'saturday'
      };
      return dayMap[dayName.toLowerCase()] || dayName.toLowerCase();
    };

    for (const employee of selectedEmployees) {
      try {
        // Use enhanced function that includes shift details
        const employeeDetails = await fetchEmployeeDetailsWithShift(employee._id || employee.id);

        if (!employeeDetails) {
          violations.push(`Unable to fetch schedule for ${employee.teamMemberName || employee.name}`);
          continue;
        }

        console.log(`Employee ${employee.teamMemberName || employee.name} details:`, employeeDetails);

        // Check weekly holidays - handle both singular and plural field names
        const taskDate = new Date(startDate);
        const taskDayName = getDayName(taskDate);
        console.log('Task date:', startDate, 'Day:', taskDayName);

        // Check both possible field names: weeklyHoliday (singular) and weeklyHolidays (plural)
        const weeklyHolidays = employeeDetails.weeklyHolidays || employeeDetails.weeklyHoliday || [];
        console.log('Employee weekly holidays:', weeklyHolidays);

        if (Array.isArray(weeklyHolidays) && weeklyHolidays.length > 0) {
          const hasWeeklyHoliday = weeklyHolidays.some(holiday => {
            const normalizedHoliday = normalizeDayName(holiday);
            return normalizedHoliday === taskDayName;
          });

          if (hasWeeklyHoliday) {
            violations.push(`${employee.teamMemberName || employee.name} has weekly holiday on ${taskDayName}s`);
          }
        }

        // ENHANCED: Check shift timing using actual shift details from API
        if (employeeDetails.shiftDetails) {
          const { startTime: shiftStart, endTime: shiftEnd, name: shiftName } = employeeDetails.shiftDetails;
          console.log(`Employee ${employee.teamMemberName || employee.name} shift ${shiftName}: ${shiftStart} - ${shiftEnd}`);

          if (shiftStart && shiftEnd) {
            try {
              // Convert times to comparable format (24-hour)
              const taskStartTime = convertTo24Hour(startTime);
              const taskEndTime = convertTo24Hour(endTime);
              const employeeShiftStart = convertTo24Hour(shiftStart);
              const employeeShiftEnd = convertTo24Hour(shiftEnd);

              console.log('Converted times - Task:', taskStartTime + '-' + taskEndTime, 'Shift:', employeeShiftStart + '-' + employeeShiftEnd);

              // Handle night shifts that cross midnight
              if (employeeShiftEnd < employeeShiftStart) {
                // Night shift (e.g., 23:00 to 08:00)
                const isTaskInNightShift = taskStartTime >= employeeShiftStart || taskEndTime <= employeeShiftEnd;
                if (!isTaskInNightShift) {
                  violations.push(`Task timing (${startTime}-${endTime}) is outside ${employee.teamMemberName || employee.name}'s ${shiftName} shift (${shiftStart}-${shiftEnd})`);
                }
              } else {
                // Regular shift within same day
                if (taskStartTime < employeeShiftStart || taskEndTime > employeeShiftEnd) {
                  violations.push(`Task timing (${startTime}-${endTime}) is outside ${employee.teamMemberName || employee.name}'s ${shiftName} shift (${shiftStart}-${shiftEnd})`);
                }
              }
            } catch (timeError) {
              console.error('Time conversion error:', timeError);
              violations.push(`Unable to validate shift timing for ${employee.teamMemberName || employee.name} - invalid time format`);
            }
          } else {
            violations.push(`${employee.teamMemberName || employee.name} has incomplete shift timing information`);
          }
        } else if (employeeDetails.shift) {
          // Fallback if shift ID exists but we couldn't fetch details
          violations.push(`Unable to fetch shift details for ${employee.teamMemberName || employee.name} (Shift ID: ${employeeDetails.shift})`);
        } else {
          violations.push(`${employee.teamMemberName || employee.name} has no shift assigned`);
        }

      } catch (error) {
        console.error('Employee schedule validation error:', error);
        violations.push(`Error validating schedule for ${employee.teamMemberName || employee.name}: ${error.message}`);
      }
    }

    return violations;
  };

  // NEW: Enhanced date validation with shift timing and holiday checks
  const validateDatesWithShiftAndHolidays = async () => {
    const errors = {};
    const shiftErrors = {};

    if (updateDates) {
      // Basic date validation
      if (!newStartDate) {
        errors.startDate = 'Start date is required';
      }

      if (!newDueDate) {
        errors.dueDate = 'Due date is required';
      }

      if (newStartDate && newDueDate) {
        const startDateTime = new Date(`${newStartDate}T${newStartTime || '00:00'}`);
        const dueDateTime = new Date(`${newDueDate}T${newDueTime || '23:59'}`);

        if (dueDateTime <= startDateTime) {
          errors.dueDate = 'Due date must be after start date';
        }

        // Check if dates are in the past
        const now = new Date();
        if (startDateTime < now) {
          errors.startDate = 'Start date cannot be in the past';
        }

        // NEW: Holiday validation for all dates
        if (isHoliday(newStartDate)) {
          errors.startDate = 'Start date cannot be scheduled on a company holiday';
        }

        if (isHoliday(newDueDate)) {
          errors.dueDate = 'Due date cannot be scheduled on a company holiday';
        }

        // NEW: Check holidays and shift timing for each selected employee
        if (selectedEmployees.length > 0) {
          try {
            console.log('Starting employee schedule validation...');
            const scheduleViolations = await validateEmployeeSchedule(
              selectedEmployees.map(id => employees.find(emp => emp._id === id)).filter(Boolean),
              newStartDate,
              newStartTime,
              newDueTime
            );

            if (scheduleViolations.length > 0) {
              shiftErrors.schedule = scheduleViolations.join('; ');
              console.log('Schedule violations found:', scheduleViolations);
            } else {
              console.log('All employees are available during task timing');
            }
          } catch (error) {
            console.error('Schedule validation error:', error);
            shiftErrors.schedule = 'Unable to validate employee schedules - please try again';
          }
        }
      }
    }

    setDateErrors(errors);
    setShiftValidationErrors(shiftErrors);
    return Object.keys(errors).length === 0 && Object.keys(shiftErrors).length === 0;
  };

  // Enhanced getCompanyId function
  const getCompanyId = useCallback(async () => {
    console.group('🔍 [DEBUG] getCompanyId() - Starting company ID resolution');
    const token = localStorage.getItem('authToken');
    console.log('📋 Token from localStorage:', token ? `${token.substring(0, 50)}...` : 'null');

    if (token) {
      try {
        console.log('🔐 Attempting to decode JWT token...');
        const decodedToken = jwtDecode(token);
        console.log('📊 Decoded token structure:', JSON.stringify(decodedToken, null, 2));

        // New logic based on role
        if (decodedToken && decodedToken.role === 'company') {
          // If role is company, use id as companyId
          if (decodedToken.id) {
            console.groupEnd();
            return decodedToken.id;
          }
        } else {
          // Otherwise, use companyId field if available
          if (decodedToken && decodedToken.companyId) {
            console.groupEnd();
            return decodedToken.companyId;
          }
        }

        // If role is not company and companyId is not present, try userId/id to fetch profile
        if (decodedToken && (decodedToken.userId || decodedToken.id)) {
          const userId = decodedToken.userId || decodedToken.id;
          console.log('👤 Employee ID from JWT:', userId);
          console.log('🔄 Attempting to fetch employee profile from MongoDB...');
        } else {
          console.warn('⚠️ No userId or id found in JWT token');
          console.log('🔍 Token payload keys available:', Object.keys(decodedToken || {}));
        }
      } catch (error) {
        console.error('❌ JWT decoding failed:', error);
        console.warn('🔄 Trying alternative methods...');
      }
    } else {
      console.warn('⚠️ No authToken found in localStorage');
      console.log('📋 All localStorage keys:', Object.keys(localStorage));
    }

    // Fallback: try localStorage
    const storedCompanyId = localStorage.getItem('companyId');
    if (storedCompanyId) {
      console.log('✅ Found companyId in localStorage:', storedCompanyId);
      console.groupEnd();
      return storedCompanyId;
    } else {
      console.warn('⚠️ No companyId found in localStorage');
    }

    console.error('❌ Unable to resolve company ID from any source');
    console.groupEnd();
    return null;
  }, []);

  // Updated getCurrentUserId function
  const getCurrentUserId = useCallback(() => {
    // Try multiple possible storage keys
    const possibleKeys = ['userId', 'user_id', 'id', 'employeeId', 'employee_id'];
    let userId = null;

    for (const key of possibleKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        userId = value.replace(/['\\\"]/g, '').trim();
        break;
      }
    }

    // Also try to extract from JWT token
    if (!userId) {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          const decoded = jwtDecode(token);
          userId = decoded.userId || decoded.id || decoded.user?.id;
        }
      } catch (error) {
        console.error('Failed to extract userId from JWT:', error);
      }
    }

    return userId;
  }, []);

  // Filter out completed tasks
  const shiftableTasks = taskArray.filter(task =>
    task.status !== 'completed' && task.status !== 'cancelled'
  );

  // NEW: Load holidays on component mount
  useEffect(() => {
    const loadHolidays = async () => {
      await fetchHolidays();
    };
    loadHolidays();
  }, []);

  // NEW: Load shift and holiday data when employees are selected - FIXED
  useEffect(() => {
    let isMounted = true;

    const loadShiftAndHolidayData = async () => {
      if (selectedEmployees.length === 0) {
        setEmployeeShifts({});
        setEmployeeHolidays({});
        return;
      }

      // Check if we already have data for all selected employees
      const needsUpdate = selectedEmployees.some(empId =>
        !employeeShifts[empId] && !employeeHolidays[empId]
      );

      if (!needsUpdate) {
        console.log('📊 Shift data already loaded for all selected employees');
        return;
      }

      setLoadingShiftData(true);
      const shiftData = {};
      const holidayData = {};

      try {
        for (const employeeId of selectedEmployees) {
          // Skip if already loaded
          if (employeeShifts[employeeId] && employeeHolidays[employeeId]) {
            shiftData[employeeId] = employeeShifts[employeeId];
            holidayData[employeeId] = employeeHolidays[employeeId];
            continue;
          }

          // Find employee data
          const employee = employees.find(emp => emp._id === employeeId);
          if (!employee) continue;

          // Fetch employee details for holidays and shifts
          const employeeDetails = await fetchEmployeeDetailsWithShift(employeeId);

          if (employeeDetails && isMounted) {
            // Store holiday data
            holidayData[employeeId] = {
              holidays: employeeDetails.holidays || [],
              weeklyHoliday: employeeDetails.weeklyHoliday || []
            };

            // Store shift data if available
            if (employeeDetails.shiftDetails) {
              shiftData[employeeId] = {
                startTime: employeeDetails.shiftDetails.startTime,
                endTime: employeeDetails.shiftDetails.endTime,
                workingDays: employeeDetails.shiftDetails.workingDays || [],
                shiftName: employeeDetails.shiftDetails.name || 'Default Shift'
              };
            }
          }
        }

        if (isMounted) {
          setEmployeeShifts(prev => ({ ...prev, ...shiftData }));
          setEmployeeHolidays(prev => ({ ...prev, ...holidayData }));
        }
      } catch (error) {
        console.error('Error loading shift and holiday data:', error);
      } finally {
        if (isMounted) {
          setLoadingShiftData(false);
        }
      }
    };

    loadShiftAndHolidayData();

    return () => {
      isMounted = false;
    };
  }, [selectedEmployees]); // CRITICAL: Only selectedEmployees, NOT employees or authToken


  // Enhanced fetch departments with async getCompanyId
  const fetchDepartments = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      const token = authToken;
      if (!companyId || !token) return;

      const response = await fetch(`${API_BASE_URL}/departments/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch departments: ${response.status}`);
      }

      const data = await response.json();
      const deptList = Array.isArray(data) ? data : (data.data || data.departments || []);
      setDepartments(deptList);

      const map = {};
      deptList.forEach(d => {
        const id = d._id || d.id;
        const name = d.name || d.departmentName;
        if (id && name) map[String(id)] = name;
      });
      setDepartmentMap(map);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  }, [getCompanyId, authToken]);

  // Helper function to get department ID from task
  const getTaskDepartmentId = (task) => {
    if (!task.department) return null;

    // Handle different department field formats
    if (typeof task.department === 'string') {
      // Check if it's an ObjectId
      if (/^[a-fA-F0-9]{24}$/.test(task.department.trim())) {
        return task.department;
      }
    } else if (Array.isArray(task.department) && task.department.length > 0) {
      const firstDept = task.department[0];
      if (typeof firstDept === 'string' && /^[a-fA-F0-9]{24}$/.test(firstDept.trim())) {
        return firstDept;
      } else if (firstDept && (firstDept._id || firstDept.id)) {
        return firstDept._id || firstDept.id;
      }
    } else if (typeof task.department === 'object' && task.department !== null) {
      return task.department._id || task.department.id;
    }

    return null;
  };

  // Enhanced fetch employees from the same department as the tasks - FIXED
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchDepartmentEmployees = async () => {
      if (taskArray.length === 0) return;

      try {
        setLoadingEmployees(true);
        const companyId = await getCompanyId();
        const token = authToken;

        if (!companyId || !token) {
          if (isMounted) setError('Missing authentication credentials');
          return;
        }

        console.log('🔄 Fetching employees for company:', companyId);

        // Fetch all employees from the company
        const response = await fetch(
          `${API_BASE_URL}/employees/company/${companyId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'x-auth-token': token
            },
            credentials: 'include',
            signal: abortController.signal
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch employees: ${response.status}`);
        }

        const data = await response.json();
        let employeesList = Array.isArray(data) ? data : (data.data || data.employees || []);

        console.log('✅ Employees fetched:', employeesList.length);

        // Exclude current assignees from all tasks
        const currentAssigneeIds = taskArray.flatMap(task => {
          if (Array.isArray(task.assignedTo)) {
            return task.assignedTo.map(id => typeof id === 'string' ? id : id._id);
          } else if (task.assignedTo) {
            return [typeof task.assignedTo === 'string' ? task.assignedTo : task.assignedTo._id];
          }
          return [];
        }).filter(Boolean);

        employeesList = employeesList.filter(emp =>
          !currentAssigneeIds.includes(emp._id)
        );

        console.log('📊 Filtered employees:', employeesList.length);

        if (isMounted) {
          setAllEmployees(employeesList);

          // Auto-select task's department if available
          if (taskArray.length > 0) {
            const taskDeptId = getTaskDepartmentId(taskArray[0]);
            if (taskDeptId) {
              setSelectedFilterDepartment(taskDeptId);
            }
          }

          setError(null);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('🚫 Fetch aborted');
          return;
        }
        console.error('❌ Error fetching employees:', error);
        if (isMounted) {
          setError('Failed to load employees from department');
        }
      } finally {
        if (isMounted) {
          setLoadingEmployees(false);
        }
      }
    };

    fetchDepartmentEmployees();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []); // CRITICAL: Empty array - only run once on mount

  // NEW: Filter employees whenever allEmployees or selectedFilterDepartment changes
  useEffect(() => {
    if (selectedFilterDepartment === 'all') {
      setEmployees(allEmployees);
      return;
    }

    const filtered = allEmployees.filter(emp => {
      if (!emp.department) return false;

      if (typeof emp.department === 'string') {
        return emp.department === selectedFilterDepartment;
      } else if (Array.isArray(emp.department)) {
        return emp.department.some(dept =>
          (typeof dept === 'string' ? dept : (dept._id || dept.id)) === selectedFilterDepartment
        );
      } else if (typeof emp.department === 'object') {
        return (emp.department._id || emp.department.id) === selectedFilterDepartment;
      }
      return false;
    });

    setEmployees(filtered);
  }, [allEmployees, selectedFilterDepartment]);



  // Initialize departments on component mount
  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // Enhanced employee name helper
  const getEmployeeName = useCallback((employee) => {
    if (!employee) return 'Unknown Employee';
    return employee.teamMemberName ||
      `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
      employee.name ||
      employee.email ||
      'Unknown Employee';
  }, []);

  // Get department name for display
  const getDepartmentName = (departmentField) => {
    if (!departmentField) return 'Unknown Department';

    const isObjectId = (val) => typeof val === 'string' && /^[a-fA-F0-9]{24}$/.test(val.trim());

    if (typeof departmentField === 'string') {
      if (isObjectId(departmentField) && departmentMap[departmentField]) {
        return departmentMap[departmentField];
      }
      return departmentField;
    } else if (Array.isArray(departmentField) && departmentField.length > 0) {
      const first = departmentField[0];
      if (typeof first === 'string') {
        if (isObjectId(first) && departmentMap[first]) return departmentMap[first];
        return first;
      }
      if (first && (first.name || first.departmentName)) {
        return first.name || first.departmentName;
      }
      if (first && (first._id || first.id)) {
        const id = first._id || first.id;
        return departmentMap[id] || 'Unknown Department';
      }
    } else if (typeof departmentField === 'object' && departmentField !== null) {
      const id = departmentField._id || departmentField.id;
      const name = departmentField.name || departmentField.departmentName;
      if (name) return name;
      if (id && departmentMap[id]) return departmentMap[id];
      return 'Unknown Department';
    }

    return 'Unknown Department';
  };

  // Handle employee selection (multiple employees)
  const handleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  // FIXED: Handle shift with date updates and validation - Add refetch + dedupe after success
  // const handleShiftTasks = async () => {
  //   if (selectedEmployees.length === 0) {
  //     setError('Please select at least one employee');
  //     return;
  //   }

  //   if (!shiftReason.trim()) {
  //     setError('Please provide a reason for shifting');
  //     return;
  //   }

  //   if (shiftableTasks.length === 0) {
  //     setError('No shiftable tasks selected');
  //     return;
  //   }

  //   // NEW: Validate dates with shift timing and holiday checks
  //   if (updateDates && !(await validateDatesWithShiftAndHolidays())) {
  //     setError('Please fix the shift timing and holiday validation errors');
  //     return;
  //   }

  //   // NEW: Warn about attachments (shift API doesn't support file uploads)
  //   if (attachments.length > 0) {
  //     if (window.confirm('Shift API does not support attachments. They will be cleared after shift. Continue?')) {
  //       // Proceed, but clear attachments later
  //     } else {
  //       return;  // User cancelled
  //     }
  //   }

  //   setLoading(true);
  //   setError('');

  //   try {
  //     // Shift all selected tasks to all selected employees (updates existing tasks + creates history)
  //     const shiftPromises = shiftableTasks.map(task => 
  //       shiftSingleTask(task._id, selectedEmployees, shiftReason)
  //     );

  //     const results = await Promise.allSettled(shiftPromises);

  //     const successfulShifts = results.filter(result => result.status === 'fulfilled').length;
  //     const failedShifts = results.filter(result => result.status === 'rejected').length;

  //     if (successfulShifts > 0) {
  //       // FIXED: Trigger parent refetch (assumes parent has fetchTasks or similar)
  //       if (onShiftSuccess) {
  //         onShiftSuccess();  // This should refetch tasks in parent component (e.g., TaskDisplay)
  //       }

  //       // NEW: Clear attachments since they weren't sent
  //       setAttachments([]);

  //       if (window.toast) {
  //         window.toast.success(
  //           `Successfully shifted ${successfulShifts} task(s) to new assignees${updateDates ? ' with updated dates' : ''}. History records created.`
  //         );
  //       }

  //       // Log successes with history details
  //       successfulShifts.forEach((result, index) => {
  //         if (result.status === 'fulfilled') {
  //           const taskId = shiftableTasks[index]?._id;
  //           const statusUpdate = result.value?.statusUpdate;
  //           console.log(`✅ Shifted task ${taskId}: New assignees applied, history ID: ${statusUpdate?._id || 'N/A'}`);
  //         }
  //       });

  //       console.log(`✅ Total shifts: ${successfulShifts} (tasks: ${shiftableTasks.map(t => t._id).join(', ')})`);
  //     }

  //     if (failedShifts > 0) {
  //       if (window.toast) {
  //         window.toast.error(`Failed to shift ${failedShifts} task(s) - Check console for details`);
  //       }
  //       // Log failures for debugging
  //       failedShifts.forEach((result, index) => {
  //         if (result.status === 'rejected') {
  //           console.error(`Shift failed for task ${shiftableTasks[index]?._id}:`, result.reason);
  //         }
  //       });
  //     }

  //   } catch (error) {
  //     console.error('Error shifting tasks:', error);
  //     setError('Failed to shift tasks - ' + error.message);
  //     if (window.toast) {
  //       window.toast.error('Failed to shift tasks');
  //     }
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // UPDATED: Handle shift with status reset
  const handleShiftTasks = async () => {
    if (selectedEmployees.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one employee',
        type: 'error'
      });
      return;
    }

    if (!shiftReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a reason for shifting',
        type: 'error'
      });
      return;
    }

    if (shiftableTasks.length === 0) {
      setSnackbar({
        open: true,
        message: 'No shiftable tasks selected',
        type: 'error'
      });
      return;
    }

    // Validate dates if update is enabled
    if (updateDates) {
      const isValid = await validateDatesWithShiftAndHolidays();
      if (!isValid) {
        setSnackbar({
          open: true,
          message: 'Please fix the date validation errors',
          type: 'error'
        });
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      console.log('🚀 Shifting tasks and resetting status...');
      console.log('├── Tasks to shift:', shiftableTasks.length);
      console.log('├── Target employees:', selectedEmployees.length);
      console.log('└── Status will be reset to: pending');

      // Shift all selected tasks
      const shiftPromises = shiftableTasks.map(task =>
        shiftSingleTask(task._id, selectedEmployees, shiftReason)
      );

      const results = await Promise.allSettled(shiftPromises);

      const successfulShifts = results.filter(result => result.status === 'fulfilled').length;
      const failedShifts = results.filter(result => result.status === 'rejected').length;

      console.log('📊 Shift results:', {
        successful: successfulShifts,
        failed: failedShifts,
        total: results.length
      });

      if (successfulShifts > 0) {
        if (onShiftSuccess) {
          onShiftSuccess();
        }

        // Build success message
        let successMessage = `Successfully shifted ${successfulShifts} task${successfulShifts !== 1 ? 's' : ''}. `;
        successMessage += 'Status reset to: Pending. ';

        if (nextFollowUpDate) {
          const followUpDateStr = new Date(nextFollowUpDate).toLocaleDateString();
          successMessage += `Next follow-up: ${followUpDateStr}`;
          if (isNextFollowUpManual) {
            successMessage += ' (Custom)';
          }
          successMessage += '. ';
        }

        if (updateDates) {
          successMessage += 'Task dates updated.';
        }

        setSnackbar({
          open: true,
          message: successMessage,
          type: 'success'
        });

        setTimeout(() => {
          onClose();
        }, 2000);
      }



      if (failedShifts > 0) {
        console.error('❌ Failed shifts:', failedShifts);

        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`Task ${shiftableTasks[index]?._id} failed:`, result.reason);
          }
        });

        setSnackbar({
          open: true,
          message: `Failed to shift ${failedShifts} task${failedShifts !== 1 ? 's' : ''}`,
          type: 'error'
        });

        setError(`Failed to shift ${failedShifts} task${failedShifts !== 1 ? 's' : ''}`);
      }

    } catch (error) {
      console.error('❌ Error shifting tasks:', error);
      setError(`Failed to shift tasks: ${error.message}`);
      setSnackbar({
        open: true,
        message: 'Failed to shift tasks',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };



  // UPDATED: Use user-selected next follow-up date (with auto-fill fallback)
  const shiftSingleTask = async (taskId, employeeIds, reason) => {
    const token = authToken;

    console.log('🔄 Starting task shift process for task:', taskId);

    // Determine next follow-up date
    let calculatedNextFollowUp = null;

    if (nextFollowUpDate) {
      // User has set a next follow-up date (manual or auto-filled)
      const followUpTime = newStartTime || '09:00'; // Use start time if available
      calculatedNextFollowUp = new Date(`${nextFollowUpDate}T${followUpTime}`).toISOString();
      console.log('📅 Using next follow-up date:', calculatedNextFollowUp, isNextFollowUpManual ? '(Manual)' : '(Auto)');
    } else if (updateDates && newStartDate && newStartTime) {
      // Fallback: Use new start date/time if no follow-up date set
      calculatedNextFollowUp = new Date(`${newStartDate}T${newStartTime}`).toISOString();
      console.log('📅 Fallback: Using start date as next follow-up:', calculatedNextFollowUp);
    } else {
      // Last fallback: Use original task start date
      const originalTask = taskArray.find(t => t._id === taskId);
      if (originalTask?.startDateTime) {
        calculatedNextFollowUp = new Date(originalTask.startDateTime).toISOString();
        console.log('📅 Using original task start date:', calculatedNextFollowUp);
      } else if (originalTask?.startDate) {
        const startTime = originalTask.startTime || '09:00';
        calculatedNextFollowUp = new Date(`${originalTask.startDate}T${startTime}`).toISOString();
        console.log('📅 Using original task startDate field:', calculatedNextFollowUp);
      }
    }

    // Step 1: Build shift payload
    const shiftPayload = {
      assignedTo: employeeIds,
      description: reason,
      shifted: true,
      isShifted: true
    };

    // Add date updates if enabled
    if (updateDates && newStartDate && newStartTime && newDueDate && newDueTime) {
      const startDateTime = new Date(`${newStartDate}T${newStartTime}`);
      const endDateTime = new Date(`${newDueDate}T${newDueTime}`);

      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        throw new Error('Invalid datetime format');
      }

      if (endDateTime <= startDateTime) {
        throw new Error('Due date must be after start date');
      }

      shiftPayload.startDateTime = startDateTime.toISOString();
      shiftPayload.endDateTime = endDateTime.toISOString();
    }

    console.log('📤 Step 1: Shifting task:', shiftPayload);

    // Execute shift
    const shiftResponse = await fetch(`${API_BASE_URL}/task/${taskId}/shifttask`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      },
      body: JSON.stringify(shiftPayload)
    });

    if (!shiftResponse.ok) {
      const errorData = await shiftResponse.json().catch(() => ({}));
      throw new Error(errorData.message || `Shift failed (${shiftResponse.status})`);
    }

    const shiftResult = await shiftResponse.json();
    console.log('✅ Step 1 Complete: Task shifted successfully');
    try {
      window.dispatchEvent(new Event('task-shifted'));
    } catch {}

    // Step 2: Update next follow-up date in task using PUT /api/task/tasks/:id
    if (calculatedNextFollowUp) {
      console.log('📤 Step 2: Updating nextFollowUp in task record');

      const taskUpdatePayload = {
        nextFollowUp: calculatedNextFollowUp,
        shifted: true,
        isShifted: true
      };

      const taskUpdateResponse = await fetch(`${API_BASE_URL}/task/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        body: JSON.stringify(taskUpdatePayload)
      });

      if (!taskUpdateResponse.ok) {
        const errorData = await taskUpdateResponse.json().catch(() => ({}));
        console.warn('⚠️ Failed to update nextFollowUp in task:', errorData.message);
      } else {
        const taskUpdateResult = await taskUpdateResponse.json();
        console.log('✅ Step 2 Complete: NextFollowUp updated in task');
        console.log('└── New nextFollowUp:', new Date(calculatedNextFollowUp).toLocaleString());
      }
    }

    // Step 3: Reset status to pending
    console.log('📤 Step 3: Resetting status to pending');

    const statusPayload = {
      status: 'pending',
      comment: `Task shifted to new employee(s). ${reason}`,
      updatedBy: getUserId()
    };

    const statusResponse = await fetch(`${API_BASE_URL}/taskstatusupdate/${taskId}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      },
      body: JSON.stringify(statusPayload)
    });

    if (!statusResponse.ok) {
      const errorData = await statusResponse.json().catch(() => ({}));
      console.warn('⚠️ Status update failed:', errorData.message);
    } else {
      console.log('✅ Step 3 Complete: Status reset to pending');
    }

    console.log('🎉 Task shift complete:');
    console.log('├── ✅ Assignees updated');
    console.log('├── ✅ Status reset to: pending');
    console.log('├── ✅ NextFollowUp:', new Date(calculatedNextFollowUp).toLocaleString());
    console.log('└── ✅ Follow-up type:', isNextFollowUpManual ? 'Manual' : 'Auto');

    return {
      ...shiftResult,
      statusReset: true,
      newStatus: 'pending',
      nextFollowUp: calculatedNextFollowUp,
      isManualFollowUp: isNextFollowUpManual
    };
  };


  // Auto-fill next follow-up date when start date changes (but allow manual override)
  useEffect(() => {
    // Only auto-fill if user hasn't manually changed it
    if (!isNextFollowUpManual && updateDates && newStartDate && newStartTime) {
      const autoDate = `${newStartDate}T${newStartTime}`;
      setNextFollowUpDate(newStartDate); // Set to date format for input
      console.log('📅 Next follow-up auto-filled to:', autoDate);
    }
  }, [newStartDate, newStartTime, updateDates, isNextFollowUpManual]);

  // Reset manual flag when dates toggle is changed
  useEffect(() => {
    if (!updateDates) {
      setIsNextFollowUpManual(false);
    }
  }, [updateDates]);


  // Helper function to reset status AND update next follow-up date
  const resetTaskStatus = async (taskId, comment, nextFollowUpDate = null) => {
    const token = authToken;

    try {
      console.log('🔄 Resetting task status and updating next follow-up...');

      // Build status update payload
      const statusPayload = {
        status: 'pending',
        comment: `Status reset: ${comment}`,
        updatedBy: getUserId()
      };

      // Add next follow-up date if provided
      if (nextFollowUpDate) {
        statusPayload.nextFollowUp = new Date(nextFollowUpDate).toISOString();
        console.log('├── Next follow-up date:', nextFollowUpDate);
      }

      console.log('📤 Status update payload:', statusPayload);

      const response = await fetch(`${API_BASE_URL}/taskstatusupdate/${taskId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        body: JSON.stringify(statusPayload)
      });

      if (!response.ok) {
        throw new Error('Status update failed');
      }

      const result = await response.json();
      console.log('✅ Status reset to pending successfully');
      if (nextFollowUpDate) {
        console.log('✅ Next follow-up date updated to:', nextFollowUpDate);
      }
      return result;

    } catch (error) {
      console.error('❌ Failed to reset status:', error);
      throw error; // Re-throw to handle in parent
    }
  };



  // Fallback function using regular PUT endpoint
  const updateTaskDirectly = async (taskId, employeeIds, reason, additionalData) => {
    const token = authToken;

    // Get original task to preserve other fields
    const originalTask = taskArray.find(t => t._id === taskId);

    const updatePayload = {
      title: originalTask.title,
      description: originalTask.description,
      assignedTo: employeeIds,
      status: 'pending', // FORCE reset to pending
      shiftReason: reason,
      ...additionalData
    };

    console.log('📤 Updating task directly with payload:', updatePayload);

    const response = await fetch(`${API_BASE_URL}/task/tasks/${taskId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      },
      body: JSON.stringify(updatePayload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Update failed (${response.status})`);
    }

    const result = await response.json();
    console.log('✅ Task updated directly, status reset to:', result.status || 'pending');

    return result;
  };




  if (taskArray.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
          <h3 className="text-lg font-semibold mb-4">No Task Selected</h3>
          <p className="text-gray-600 mb-4">Please select a task to shift.</p>
          <button onClick={onClose} className="bg-blue-500 text-white px-4 py-2 rounded">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">

      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" style={{ background: '#F8F9FA' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                Shift {shiftableTasks.length} Task{shiftableTasks.length !== 1 ? 's' : ''}
              </h2>
              <p className="text-purple-200 text-sm">
                Assign tasks to multiple employees{updateDates ? ' and update dates' : ''}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-purple-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(90vh-80px)] overflow-y-auto">
          {/* NEW: Holiday Loading/Error State */}
          {(loadingHolidays || holidayError) && (
            <div className={`rounded-xl p-4 border mb-6 ${holidayError ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${holidayError ? 'bg-red-100' : 'bg-blue-100'}`}>
                  {loadingHolidays ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  ) : (
                    <svg className={`w-4 h-4 ${holidayError ? 'text-red-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={holidayError ? "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" : "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"} />
                    </svg>
                  )}
                </div>
                <div>
                  <p className={`text-sm font-medium ${holidayError ? 'text-red-800' : 'text-blue-800'}`}>
                    {loadingHolidays ? 'Loading company holidays...' : 'Holiday data unavailable'}
                  </p>
                  {holidayError && (
                    <p className="text-xs text-red-600 mt-1">
                      {holidayError}
                      <button
                        type="button"
                        onClick={fetchHolidays}
                        className="ml-2 underline hover:no-underline"
                      >
                        Retry
                      </button>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* NEW: Schedule validation errors */}
          {shiftValidationErrors.schedule && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-yellow-800 mb-1">Schedule Conflicts Detected</h4>
                  <p className="text-sm text-yellow-700">{shiftValidationErrors.schedule}</p>
                </div>
              </div>
            </div>
          )}

          {/* Task List */}
          <div className="mb-6 bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Selected Tasks ({taskArray.length})
            </h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {taskArray.map((task, index) => (
                <div
                  key={task._id}
                  className={`p-3 rounded-lg border ${task.status === 'completed' || task.status === 'cancelled'
                    ? 'bg-gray-100 border-gray-300 text-gray-500'
                    : 'bg-blue-50 border-blue-200'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <span className="font-medium truncate">{task.title}</span>
                      <div className="text-xs text-gray-500 mt-1">
                        Department: {getDepartmentName(task.department)}
                        {/* NEW: Show current dates */}
                        <div className="mt-1">
                          Start: {task.startDate
                            ? new Date(task.startDate).toLocaleDateString('en-GB')
                            : new Date(task.startDateTime).toLocaleDateString('en-GB')} •
                          Due: {task.deadline
                            ? new Date(task.deadline).toLocaleDateString('en-GB')
                            : new Date(task.endDateTime).toLocaleDateString('en-GB')}
                        </div>

                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${task.status === 'completed' || task.status === 'cancelled'
                      ? 'bg-gray-200 text-gray-600'
                      : 'bg-green-100 text-green-800'
                      }`}>
                      {task.status}
                    </span>
                  </div>
                  {task.status === 'completed' || task.status === 'cancelled' ? (
                    <p className="text-xs text-red-500 mt-1">Cannot shift completed/cancelled tasks</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          {/* Date Update Section */}
          <div className="mb-6 bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700 flex items-center">
                <svg className="w-4 h-4 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Update Task Dates
              </h3>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">Keep original dates</span>
                <div
                  className={`relative w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors ${updateDates ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  onClick={() => setUpdateDates(!updateDates)}
                >
                  <div className={`bg-white w-4 h-4 rounded-full shadow transform transition-transform ${updateDates ? 'translate-x-6' : ''}`} />
                </div>
                <span className="text-sm text-gray-600">Update dates</span>
              </div>
            </div>

            {/* FIXED: Only show date inputs when updateDates is TRUE */}
            {updateDates && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* New Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Start Date & Time <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="date"
                          value={newStartDate}
                          onChange={(e) => {
                            setNewStartDate(e.target.value);
                            setDateErrors({ ...dateErrors, startDate: '' });
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${dateErrors.startDate ? 'border-red-500' : 'border-gray-300'
                            } ${isHoliday(newStartDate) ? 'bg-red-50' : ''}`}
                          required
                        />
                        {isHoliday(newStartDate) && (
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <input
                        type="time"
                        value={newStartTime}
                        onChange={(e) => setNewStartTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        required
                      />
                    </div>
                    {dateErrors.startDate && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        {dateErrors.startDate}
                      </p>
                    )}
                  </div>

                  {/* New Due Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Due Date & Time <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="date"
                          value={newDueDate}
                          onChange={(e) => {
                            setNewDueDate(e.target.value);
                            setDateErrors({ ...dateErrors, dueDate: '' });
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${dateErrors.dueDate ? 'border-red-500' : 'border-gray-300'
                            } ${isHoliday(newDueDate) ? 'bg-red-50' : ''}`}
                          required
                        />
                        {isHoliday(newDueDate) && (
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <input
                        type="time"
                        value={newDueTime}
                        onChange={(e) => setNewDueTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        required
                      />
                    </div>
                    {dateErrors.dueDate && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        {dateErrors.dueDate}
                      </p>
                    )}
                  </div>
                </div>

                {/* Date Preview */}
                {newStartDate && newDueDate && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center text-sm text-blue-800">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Tasks will be updated: {new Date(newStartDate).toLocaleDateString()} at {newStartTime} → {new Date(newDueDate).toLocaleDateString()} at {newDueTime}
                      {(isHoliday(newStartDate) || isHoliday(newDueDate)) && (
                        <span className="ml-2 text-red-600 font-medium">⚠️ Holiday conflict!</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show message when toggle is OFF */}
            {!updateDates && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-600 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Tasks will keep their original start and due dates
                </p>
              </div>
            )}
          </div>


          {/* Employee Selection */}
          <div className="mb-6 bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              Select Employees ({selectedEmployees.length} selected) <span className="text-red-500 ml-1">*</span>
            </h3>

            {/* NEW: Department Filter Dropdown */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                Filter by Department
              </label>
              <div className="relative">
                <select
                  value={selectedFilterDepartment}
                  onChange={(e) => setSelectedFilterDepartment(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-medium transition-all appearance-none outline-none"
                >
                  <option value="all">All Departments</option>
                  {departments.map((dept) => (
                    <option key={dept._id || dept.id} value={dept._id || dept.id}>
                      {dept.name || dept.departmentName}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1" />
                  </svg>
                </div>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {loadingEmployees ? (
              <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                <svg className="animate-spin mr-3 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-gray-500">Loading employees...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                {employees.map(employee => (
                  <label
                    key={employee._id}
                    className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(employee._id)}
                      onChange={() => handleEmployeeSelection(employee._id)}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getEmployeeName(employee)}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{employee.email}</p>
                      <p className="text-xs text-gray-400">
                        Department: {getDepartmentName(employee.department)}
                      </p>

                      {/* NEW: Show shift information */}
                      {employeeShifts[employee._id] && (
                        <div className="text-xs text-blue-600 mt-1">
                          Shift: {employeeShifts[employee._id].startTime} - {employeeShifts[employee._id].endTime}
                          ({employeeShifts[employee._id].shiftName})
                        </div>
                      )}

                      {/* NEW: Show loading shift data */}
                      {selectedEmployees.includes(employee._id) && loadingShiftData && (
                        <div className="text-xs text-yellow-600 mt-1 flex items-center">
                          <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading shift data...
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}

            {employees.length === 0 && !loadingEmployees && (
              <p className="mt-2 text-xs text-gray-500 flex items-center">
                <svg className="w-4 h-4 mr-1 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                No available employees found for the selected department
              </p>
            )}
          </div>

          {/* Reason Input */}
          <div className="mb-6 bg-white rounded-xl p-4 border border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
              <svg className="w-4 h-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              Reason for Shifting <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              value={shiftReason}
              onChange={(e) => setShiftReason(e.target.value)}
              placeholder="Explain why you're shifting these tasks..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows="3"
              required
            />
          </div>

          {/* Next Follow-Up Date - User Editable with Auto-fill */}
          <div className="mb-6 bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700 flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Next Follow-Up Date
              </label>

            </div>

            <div className="space-y-2">
              {/* Date Input */}
              <input
                type="date"
                value={nextFollowUpDate}
                onChange={(e) => {
                  setNextFollowUpDate(e.target.value);
                  setIsNextFollowUpManual(true); // Mark as manually changed
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />





              {/* Reset to Auto Button */}
              {isNextFollowUpManual && updateDates && newStartDate && (
                <button
                  type="button"
                  onClick={() => {
                    setNextFollowUpDate(newStartDate);
                    setIsNextFollowUpManual(false);
                  }}
                  className="text-xs text-indigo-600 hover:text-indigo-800 underline flex items-center"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset to start date
                </button>
              )}
            </div>
          </div>


          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleShiftTasks}
              disabled={loading || shiftableTasks.length === 0 || selectedEmployees.length === 0 || !shiftReason.trim()}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Shifting...
                </span>
              ) : (
                `Shift ${shiftableTasks.length} Task${shiftableTasks.length !== 1 ? 's' : ''}${updateDates ? ' & Update Dates' : ''}`
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        position="top-right"
        duration={4000}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={dialogLoading}
      />
    </div>
  );
};

export default ShiftTaskModal;
